import React, { useState, useEffect, useCallback } from 'react';
import { authFetch } from '../../context/AuthContext';

/* ── 타입 ── */
interface Inquiry {
  id: number;
  contact_name: string;
  company: string | null;
  phone: string;
  email: string;
  product_type: string | null;
  quantity_range: string | null;
  message: string | null;
  status: string;          // string으로 확장 — 마이그레이션 전 구버전 값 허용
  admin_notes: string | null;
  created_at: string;
  updated_at: string | null;
}

interface Summary {
  total: number;
  waiting: number;
  confirmed: number;
  hold: number;
  completed: number;
}

/* ── 상태 설정 ── */
const STATUS_CONFIG: Record<string, { label: string; color: string; dot: string; btn: string }> = {
  /* 현재 값 */
  waiting:   { label: '대기',  color: 'bg-stone-100 text-stone-600',   dot: 'bg-stone-400',  btn: 'bg-stone-100 hover:bg-stone-200 text-stone-700' },
  confirmed: { label: '확인',  color: 'bg-blue-100 text-blue-700',     dot: 'bg-blue-500',   btn: 'bg-blue-100 hover:bg-blue-200 text-blue-700' },
  hold:      { label: '보류',  color: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500', btn: 'bg-orange-100 hover:bg-orange-200 text-orange-700' },
  completed: { label: '완료',  color: 'bg-green-100 text-green-700',   dot: 'bg-green-500',  btn: 'bg-green-100 hover:bg-green-200 text-green-700' },
  /* 마이그레이션 전 구버전 값 → 동일 스타일로 fallback */
  new:       { label: '대기',  color: 'bg-stone-100 text-stone-600',   dot: 'bg-stone-400',  btn: 'bg-stone-100 hover:bg-stone-200 text-stone-700' },
  reviewing: { label: '확인',  color: 'bg-blue-100 text-blue-700',     dot: 'bg-blue-500',   btn: 'bg-blue-100 hover:bg-blue-200 text-blue-700' },
  replied:   { label: '보류',  color: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500', btn: 'bg-orange-100 hover:bg-orange-200 text-orange-700' },
  closed:    { label: '완료',  color: 'bg-green-100 text-green-700',   dot: 'bg-green-500',  btn: 'bg-green-100 hover:bg-green-200 text-green-700' },
};

/* 알 수 없는 상태 fallback */
const FALLBACK_CFG = { label: '대기', color: 'bg-stone-100 text-stone-600', dot: 'bg-stone-400', btn: 'bg-stone-100 hover:bg-stone-200 text-stone-700' };
const getCfg = (status: string) => STATUS_CONFIG[status] ?? FALLBACK_CFG;

/* 마이그레이션 전 구버전 status → 신버전으로 정규화 */
const normalizeStatus = (status: string): string => {
  const map: Record<string, string> = { new: 'waiting', reviewing: 'confirmed', replied: 'hold', closed: 'completed' };
  return map[status] ?? status;
};

const StatusBadge = ({ status }: { status: string }) => {
  const cfg = getCfg(status);
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${cfg.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`}/>
      {cfg.label}
    </span>
  );
};

/* ── 날짜 포맷 ── */
const fmtDate = (d: string) => d.slice(0, 16).replace('T', ' ');

export default function InquiryPage() {
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [summary,   setSummary]   = useState<Summary>({ total:0, waiting:0, confirmed:0, hold:0, completed:0 });
  const [loading,   setLoading]   = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search,    setSearch]    = useState('');
  const [selected,  setSelected]  = useState<Inquiry | null>(null);
  const [notes,     setNotes]     = useState('');
  const [saving,    setSaving]    = useState(false);
  const [toast,     setToast]     = useState('');

  const flash = (msg: string) => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  /* ── 데이터 로드 ── */
  const load = useCallback(async () => {
    setLoading(true);
    const qs = [
      statusFilter !== 'all' ? `status=${statusFilter}` : '',
      search ? `search=${encodeURIComponent(search)}` : '',
    ].filter(Boolean).join('&');

    const [inq, sum] = await Promise.all([
      authFetch(`/api/oem${qs ? '?' + qs : ''}`).then(r => r.ok ? r.json() : []),
      authFetch('/api/oem/summary').then(r => r.ok ? r.json() : { total:0, waiting:0, confirmed:0, hold:0, completed:0 }),
    ]);
    setInquiries(inq);
    setSummary(sum);
    setLoading(false);
  }, [statusFilter, search]);

  useEffect(() => { load(); }, [load]);

  /* ── 상태 변경 ── */
  const changeStatus = async (id: number, status: string, noteVal?: string) => {
    setSaving(true);
    const body: Record<string, string> = { status };
    if (noteVal !== undefined) body.admin_notes = noteVal;

    const res  = await authFetch(`/api/oem/${id}/status`, { method: 'PATCH', body: JSON.stringify(body) });
    const data = await res.json();
    setSaving(false);
    if (!res.ok) return;

    setInquiries(prev => prev.map(i => i.id === id ? data : i));
    if (selected?.id === id) setSelected(data);
    flash(`상태가 "${STATUS_CONFIG[status as keyof typeof STATUS_CONFIG]?.label}"(으)로 변경되었습니다.`);
    await load();
  };

  /* ── 메모 저장 ── */
  const saveNotes = async () => {
    if (!selected) return;
    setSaving(true);
    const res  = await authFetch(`/api/oem/${selected.id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ admin_notes: notes }),
    });
    const data = await res.json();
    setSaving(false);
    if (!res.ok) return;
    setSelected(data);
    setInquiries(prev => prev.map(i => i.id === selected.id ? data : i));
    flash('메모가 저장되었습니다.');
  };

  /* ── 상세 열기 ── */
  const openDetail = (inq: Inquiry) => {
    setSelected(inq);
    setNotes(inq.admin_notes ?? '');
  };

  /* ── 상태 탭 ── */
  const tabs = [
    { key: 'all',       label: '전체',   count: summary.total },
    { key: 'waiting',   label: '대기',   count: summary.waiting,   dot: 'bg-stone-400' },
    { key: 'confirmed', label: '확인',   count: summary.confirmed, dot: 'bg-blue-500' },
    { key: 'hold',      label: '보류',   count: summary.hold,      dot: 'bg-orange-500' },
    { key: 'completed', label: '완료',   count: summary.completed, dot: 'bg-green-500' },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">

      {toast && (
        <div className="mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
          {toast}
        </div>
      )}

      {/* 헤더 */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-stone-900">제작 문의 관리</h1>
        <p className="text-stone-500 text-sm mt-1">OEM/ODM 문의 접수 현황 및 처리 관리</p>
      </div>

      {/* 요약 카드 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        {[
          { label: '전체 문의',  value: summary.total,     color: 'bg-white' },
          { label: '대기',       value: summary.waiting,   color: 'bg-stone-50',   text: 'text-stone-700' },
          { label: '확인',       value: summary.confirmed, color: 'bg-blue-50',    text: 'text-blue-700' },
          { label: '보류',       value: summary.hold,      color: 'bg-orange-50',  text: 'text-orange-700' },
          { label: '완료',       value: summary.completed, color: 'bg-green-50',   text: 'text-green-700' },
        ].map(c => (
          <div key={c.label} className={`${c.color} rounded-xl p-4 shadow-sm text-center`}>
            <p className={`text-2xl font-bold ${(c as any).text ?? 'text-stone-800'}`}>{c.value}</p>
            <p className="text-xs text-stone-500 mt-0.5">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* ── 목록 패널 ── */}
        <div className="lg:col-span-1 bg-white rounded-2xl shadow-sm overflow-hidden flex flex-col">

          {/* 상태 탭 */}
          <div className="flex border-b border-stone-100 overflow-x-auto">
            {tabs.map(t => (
              <button key={t.key} onClick={() => setStatusFilter(t.key)}
                className={`flex-1 min-w-0 py-3 px-2 text-xs font-semibold transition flex flex-col items-center gap-0.5 ${
                  statusFilter === t.key ? 'border-b-2 border-amber-500 text-amber-600 bg-amber-50' : 'text-stone-500 hover:bg-stone-50'
                }`}>
                <span className="flex items-center gap-1">
                  {(t as any).dot && <span className={`w-1.5 h-1.5 rounded-full ${(t as any).dot}`}/>}
                  {t.label}
                </span>
                <span className={`font-bold text-sm ${statusFilter === t.key ? 'text-amber-600' : 'text-stone-700'}`}>{t.count}</span>
              </button>
            ))}
          </div>

          {/* 검색 */}
          <div className="p-3 border-b border-stone-100">
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="이름·회사·이메일·연락처 검색..."
              className="w-full border border-stone-200 rounded-lg px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-amber-400" />
          </div>

          {/* 문의 목록 */}
          <div className="flex-1 overflow-y-auto divide-y divide-stone-50">
            {loading ? (
              <div className="text-center py-12 text-stone-400 text-sm animate-pulse">불러오는 중...</div>
            ) : inquiries.length === 0 ? (
              <div className="text-center py-12 text-stone-400 text-sm">
                <p className="text-3xl mb-2">📭</p>
                문의가 없습니다.
              </div>
            ) : inquiries.map(inq => (
              <button key={inq.id} onClick={() => openDetail(inq)}
                className={`w-full text-left px-4 py-3.5 hover:bg-stone-50 transition ${selected?.id === inq.id ? 'bg-amber-50 border-l-2 border-amber-500' : ''}`}>
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <div className="min-w-0">
                    <p className="font-semibold text-stone-800 text-sm truncate">{inq.contact_name}</p>
                    {inq.company && <p className="text-xs text-stone-400 truncate">{inq.company}</p>}
                  </div>
                  <StatusBadge status={inq.status} />
                </div>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-stone-400 truncate">{inq.product_type ?? '제품 유형 미입력'}</p>
                  <p className="text-xs text-stone-300 flex-shrink-0 ml-2">{inq.created_at.slice(5, 10)}</p>
                </div>
                {inq.admin_notes && (
                  <p className="text-xs text-amber-600 mt-1 truncate">📝 {inq.admin_notes}</p>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* ── 상세 패널 ── */}
        <div className="lg:col-span-2">
          {!selected ? (
            <div className="bg-white rounded-2xl shadow-sm h-full flex items-center justify-center text-stone-400 min-h-[400px]">
              <div className="text-center">
                <p className="text-5xl mb-3">👈</p>
                <p className="text-sm">왼쪽 목록에서 문의를 선택하세요</p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">

              {/* 상세 헤더 */}
              <div className="bg-stone-900 text-white px-6 py-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h2 className="text-lg font-bold">{selected.contact_name}</h2>
                      <StatusBadge status={selected.status} />
                    </div>
                    {selected.company && <p className="text-stone-400 text-sm">{selected.company}</p>}
                    <p className="text-stone-500 text-xs mt-1">접수: {fmtDate(selected.created_at)}</p>
                  </div>
                  <div className="text-right text-xs text-stone-400">
                    <p>#{selected.id}</p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-6">

                {/* 연락처 정보 */}
                <div>
                  <h3 className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-3">연락처 정보</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { label: '연락처', value: selected.phone },
                      { label: '이메일', value: selected.email },
                      { label: '제품 유형', value: selected.product_type ?? '-' },
                      { label: '예상 수량', value: selected.quantity_range ?? '-' },
                    ].map(f => (
                      <div key={f.label} className="bg-stone-50 rounded-xl p-3">
                        <p className="text-xs text-stone-400 mb-0.5">{f.label}</p>
                        <p className="text-sm font-semibold text-stone-800">{f.value}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 문의 내용 */}
                {selected.message && (
                  <div>
                    <h3 className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-3">문의 내용</h3>
                    <div className="bg-stone-50 rounded-xl p-4">
                      <p className="text-sm text-stone-700 leading-relaxed whitespace-pre-wrap">{selected.message}</p>
                    </div>
                  </div>
                )}

                {/* 상태 변경 — 신버전 4개 고정 */}
                <div>
                  <h3 className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-3">처리 상태 변경</h3>
                  <div className="flex flex-wrap gap-2">
                    {(['waiting','confirmed','hold','completed'] as const).map(key => {
                      const cfg    = STATUS_CONFIG[key];
                      const isCurr = normalizeStatus(selected.status) === key;
                      return (
                        <button key={key}
                          onClick={() => changeStatus(selected.id, key)}
                          disabled={saving || isCurr}
                          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition disabled:opacity-50 ${
                            isCurr ? cfg.color + ' ring-2 ring-offset-1 ring-current' : cfg.btn
                          }`}>
                          <span className={`w-2 h-2 rounded-full ${cfg.dot}`}/>
                          {cfg.label}
                          {isCurr && ' ✓'}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* 관리자 메모 */}
                <div>
                  <h3 className="text-xs font-bold text-stone-500 uppercase tracking-wider mb-3">관리자 메모</h3>
                  <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4}
                    className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
                    placeholder="내부 처리 메모, 담당자 연락 기록, 특이사항 등..." />
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-xs text-stone-400">
                      {selected.updated_at ? `마지막 수정: ${fmtDate(selected.updated_at)}` : ''}
                    </p>
                    <button onClick={saveNotes} disabled={saving || notes === (selected.admin_notes ?? '')}
                      className="bg-amber-500 hover:bg-amber-600 disabled:opacity-40 text-black font-bold px-4 py-2 rounded-lg text-sm transition">
                      {saving ? '저장 중...' : '메모 저장'}
                    </button>
                  </div>
                </div>

                {/* 빠른 응대 — 이메일 링크 */}
                <div className="border-t border-stone-100 pt-4 flex flex-wrap gap-2">
                  <a href={`mailto:${selected.email}?subject=COBRAND 제작 문의 답변 (${selected.contact_name}님)`}
                    onClick={() => changeStatus(selected.id, 'confirmed')}
                    className="flex items-center gap-2 bg-stone-800 hover:bg-stone-900 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition">
                    📧 이메일 답변
                  </a>
                  <a href={`tel:${selected.phone}`}
                    className="flex items-center gap-2 border border-stone-200 hover:border-amber-400 hover:text-amber-600 text-stone-600 text-sm font-semibold px-4 py-2.5 rounded-xl transition">
                    전화 연결
                  </a>
                  <button onClick={() => { changeStatus(selected.id, 'completed'); setSelected(null); }}
                    className="flex items-center gap-2 bg-green-50 hover:bg-green-100 text-green-700 text-sm font-semibold px-4 py-2.5 rounded-xl transition ml-auto">
                    처리 완료
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
